import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Select, { MultiValue } from "react-select";
import { Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt, faTrash } from "@fortawesome/free-solid-svg-icons";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { useAuth } from "../../../../../firebase/AuthProvider";
import TemplateTask from "../../../../../interfaces/templateTask/TemplateTask";
import TemplateTaskAction from "../../../../../interfaces/templateTask/TemplateTaskAction";
import TemplateRequiresFile from "../../../../../interfaces/templateTask/TemplateRequiresFile";
import Tag from "../../../../../interfaces/tag/Tag";
import {
    getTemplateTask,
    getTemplateTaskRequiredFiles,
    deleteTemplateTaskRequiredFile,
    deleteTemplateTask,
    getAllTemplateTasks,
} from "../../../../../services/templateService/TemplateTaskService";
import {
    getTemplateTaskActions,
    deleteTemplateTaskAction,
    publishTemplateTaskTags,
} from "../../../../../services/templateService/TemplateTaskActionService";
import {
    createTemplateTaskRelationship,
    deleteTemplateTaskRelationship,
    getTemplateTaskRelationships,
    getTaskRelationshipTypes,
} from "../../../../../services/TaskRelationships/TaskRelationshipService";
import {
    CreateRelationshipDto,
    TaskRelationshipType,
    TemplateRelationshipResponse,
} from "../../../../../interfaces/taskRelationship/taskRelationship";
import { getAllTags } from "../../../../../services/tagServices/TagService";
import { createAdministrativeTransactionRequest } from "../../../../../services/AdministrativeRequestServices/AdministrativeRequestService";
import { getInterimTemplates } from "../../../../../services/taskServices/TaskService";

import AddTaskAction from "../AddTaskAction";
import AddRequiredFile from "../AddRequiredFile";
import LoadingSpinner from "../../../../ui/Loading/LoadingSpinner";

import backarrowbtn from '../../../../../assets/images/backarrowbtn.svg';
import pdficon from '../../../../../assets/images/pdficon.svg';
import trashicons from '../../../../../assets/images/trashicons.svg';
import TemplateTaskShortResponse from "../../../../../interfaces/templateTask/TemplateTaskShortResponse";
import UpdateInformation from "./UpdateInformation";

interface OptionType {
    value: string;
    label: string;
}

interface TemplateTaskEditorProps {
    templateTaskId: number;
    onBack: () => void;
}

const TemplateTaskEditor: React.FC<TemplateTaskEditorProps> = ({ templateTaskId, onBack }) => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    const [templateTask, setTemplateTask] = useState<TemplateTask>();
    const [templateTaskActions, setTemplateTaskActions] = useState<TemplateTaskAction[]>([]);
    const [templateFiles, setTemplateFiles] = useState<TemplateRequiresFile[]>([]);
    const [allTags, setAllTags] = useState<Tag[]>([]);
    const [tagOptions, setTagOptions] = useState<OptionType[]>([]);
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [reload, setReload] = useState<boolean>(false);

    // State for relationships
    const [existingRelationships, setExistingRelationships] = useState<TemplateRelationshipResponse[]>([]);
    const [initialRelationships, setInitialRelationships] = useState<TemplateRelationshipResponse[]>([]);
    const [relationshipTypes, setRelationshipTypes] = useState<TaskRelationshipType[]>([]);
    const [allInterimTemplates, setAllInterimTemplates] = useState<TemplateTaskShortResponse[]>([]);
    const [newRelationshipTargetId, setNewRelationshipTargetId] = useState<number | null>(null);
    const [newRelationshipTypeId, setNewRelationshipTypeId] = useState<number | null>(null);
    const [isAddingRelationship, setIsAddingRelationship] = useState(false);

    useEffect(() => {
        const fetchAllTags = async () => {
            if (currentUser) {
                const authToken = await currentUser.getIdToken();
                const tags = await getAllTags(authToken);
                if (tags) setAllTags(tags);
            }
        };
        fetchAllTags();
    }, [currentUser]);

    useEffect(() => {
        if (allTags.length > 0) {
            const formatted = allTags.map((t) => ({
                value: t.id.toString(),
                label: t.name,
            }));
            setTagOptions(formatted);
        }
    }, [allTags]);

    useEffect(() => {
        const fetchTaskTemplate = async () => {
            if (currentUser && templateTaskId) {
                const authToken = await currentUser.getIdToken();
                const [task, actions, files] = await Promise.all([
                    getTemplateTask(templateTaskId, authToken),
                    getTemplateTaskActions(templateTaskId, authToken),
                    getTemplateTaskRequiredFiles(templateTaskId, authToken),
                ]);
                if (task) setTemplateTask(task);
                if (actions) setTemplateTaskActions(actions);
                if (files) setTemplateFiles(files);
            }
        };
        fetchTaskTemplate();
    }, [templateTaskId, currentUser, reload]);

    // Fetch Existing Relationships
    useEffect(() => {
        if (currentUser && templateTaskId) {
            const fetchRelationships = async () => {
                try {
                    const authToken = await currentUser.getIdToken();
                    const relationships = await getTemplateTaskRelationships(authToken, templateTaskId);
                    if (relationships) {
                        setExistingRelationships(relationships);
                        setInitialRelationships(relationships);
                    }
                } catch (error) {
                    console.error("Error fetching relationships:", error);
                }
            };
            fetchRelationships();
        }
    }, [currentUser, templateTaskId]);

    // Fetch Relationship Types
    useEffect(() => {
        if (currentUser) {
            const fetchRelationshipTypes = async () => {
                try {
                    const authToken = await currentUser.getIdToken();
                    const types = await getTaskRelationshipTypes(authToken);
                    if (types) {
                        setRelationshipTypes(types);
                    }
                } catch (error) {
                    console.error("Error fetching relationship types:", error);
                }
            };
            fetchRelationshipTypes();
        }
    }, [currentUser]);

    // Fetch All Interim Templates
    useEffect(() => {
        if (currentUser) {
            const fetchAllTemplates = async () => {
                try {
                    const authToken = await currentUser.getIdToken();
                    const templates = await getInterimTemplates(authToken);
                    if (templates) {
                        // Exclude the current template from the list
                        setAllInterimTemplates(templates.filter(t => t.id !== templateTaskId));
                    }
                } catch (error) {
                    console.error("Error fetching interim templates:", error);
                }
            };
            fetchAllTemplates();
        }
    }, [currentUser, templateTaskId]);


    useEffect(() => {
        if (templateTask?.tags) {
            const existingTagIds = templateTask.tags.map((tag) => tag.id);
            setSelectedTagIds(existingTagIds);
        }
    }, [templateTask]);

    const reloadFn = () => setReload(!reload);

    const handleTagChange = (selectedOptions: MultiValue<OptionType>) => {
        const newIds = selectedOptions.map((opt) => parseInt(opt.value));
        setSelectedTagIds(newIds);
    };

    const submitTaskTags = async () => {
        if (templateTask && currentUser) {
            try {
                const authToken = await currentUser.getIdToken();
                await publishTemplateTaskTags(selectedTagIds, templateTask.id, authToken);
                reloadFn();
            } catch (error) {
                console.error("An error occurred while updating tags:", error);
            }
        }
    };

    const deleteTaskFile = async (fileId: number) => {
        if (currentUser && fileId) {
            const authToken = await currentUser.getIdToken();
            await deleteTemplateTaskRequiredFile(fileId, authToken);
            reloadFn();
        }
    };

    const deleteTaskAction = async (actionId: number) => {
        if (currentUser && actionId) {
            const authToken = await currentUser.getIdToken();
            await deleteTemplateTaskAction(actionId, authToken);
            reloadFn();
        }
    };

    const confirmDelete = async () => {
        setIsLoading(true);
        try {
            if (currentUser && templateTaskId) {
                const authToken = await currentUser.getIdToken();
                const result = await deleteTemplateTask(templateTaskId, authToken);

                if (result.success) {
                    toast.success("Recurring task deleted successfully");
                    window.dispatchEvent(new CustomEvent("recurring-tasks-updated"));
                    onBack();
                } else {
                    toast.error(result.error || "Failed to delete recurring task.");
                }
            }
        } catch (error) {
            toast.error("An unexpected error occurred while deleting the task.");
            console.error("Error deleting the template task:", error);
        } finally {
            setIsLoading(false);
            setShowDeleteModal(false);
        }
    };

    const handleAddRelationship = () => {
        if (newRelationshipTargetId && newRelationshipTypeId) {
            // Check for duplicates
            const alreadyExists = existingRelationships.some(
                (rel) => rel.relationshipTypeId === newRelationshipTypeId && rel.otherTemplateId === newRelationshipTargetId
            );

            if (alreadyExists) {
                alert("This exact relationship already exists.");
                return;
            }
            const targetTemplate = allInterimTemplates.find(t => t.id === newRelationshipTargetId);
            const relationshipType = relationshipTypes.find(rt => rt.id === newRelationshipTypeId);

            if (targetTemplate && relationshipType) {
                const newRelationship: TemplateRelationshipResponse = {
                    otherTemplateId: targetTemplate.id,
                    otherTemplateTitle: targetTemplate.title,
                    relationshipTypeId: relationshipType.id,
                    relationshipName: relationshipType.name,
                    isSource: true, // Assuming the new relationship is always from the source
                    sourceTemplateId: templateTaskId,
                    targetTemplateId: targetTemplate.id,
                };
                setExistingRelationships([...existingRelationships, newRelationship]);
                setNewRelationshipTargetId(null);
                setNewRelationshipTypeId(null);
                setIsAddingRelationship(false); // Hide the form
            }
        }
    };

    const handleRemoveRelationship = (targetId: number, typeId: number) => {
        setExistingRelationships(
            existingRelationships.filter(
                (rel) => !(rel.otherTemplateId === targetId && rel.relationshipTypeId === typeId)
            )
        );
    };

    // Submit
    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const handleUpdate = async () => {
            if (!currentUser) return;

            try {
                const authToken = await currentUser.getIdToken();

                // 2. Determine added and removed relationships
                const initialRels = new Set(initialRelationships.map(r => `${r.otherTemplateId}-${r.relationshipTypeId}`));
                const currentRels = new Set(existingRelationships.map(r => `${r.otherTemplateId}-${r.relationshipTypeId}`));

                const addedRels = existingRelationships.filter(r => !initialRels.has(`${r.otherTemplateId}-${r.relationshipTypeId}`));
                const removedRels = initialRelationships.filter(r => !currentRels.has(`${r.otherTemplateId}-${r.relationshipTypeId}`));

                // 3. Create promises for all API calls
                const createPromises = addedRels.map(rel => {
                    const dto: CreateRelationshipDto = {
                        targetTemplateId: rel.otherTemplateId,
                        relationshipTypeId: rel.relationshipTypeId,
                    };
                    return createTemplateTaskRelationship(authToken, templateTaskId, dto);
                });

                const deletePromises = removedRels.map(rel =>
                    deleteTemplateTaskRelationship(authToken, templateTaskId, rel.otherTemplateId, rel.relationshipTypeId)
                );

                // 4. Execute all promises
                await Promise.all([...createPromises, ...deletePromises]);

                // 5. On success, navigate away
                onBack();

            } catch (error) {
                console.error("Failed to update interim template or its relationships:", error);
            }
        };

        handleUpdate();
    };

    console.log('Template Task', templateTask)
    console.log('Template Files', templateFiles)

    return (
        <>
            <div className="top top_bar_flx">
                <div className="top_bar_tittle">
                    <h2 onClick={onBack}><img className="cursor-pointer" style={{ width: '1.5rem' }} src={backarrowbtn} alt="backarrowbtn" />Edit</h2>
                </div>
                <div className="top_right_flx">
                    <Button className="btn_sm border-0" variant="primary" onClick={() => setShowDeleteModal(true)}>Delete Template</Button>
                </div>
            </div>

            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="custom_popup delete_popup" size="sm">
                <Modal.Header closeButton>
                    <Modal.Title>Delete Confirmation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-center">Are you sure you want to delete this task?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={confirmDelete} disabled={isLoading}>
                        {isLoading ? <LoadingSpinner color="white" size="sm" /> : "Delete"}
                    </Button>
                </Modal.Footer>
            </Modal>

            {templateTask && (
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mb-3">
                            <div className="common_card h-100">
                                <div className="top border_tittle">
                                    <div className="top_bar_tittle">
                                        <h2>Template: {templateTask?.title}</h2>
                                    </div>
                                </div>
                                <div className="edit_template_grid mby_20">
                                    <div className="stats">
                                        <h3>Template ID</h3>
                                        <p>{templateTask.id}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Title</h3>
                                        <p>{templateTask.title}</p>
                                    </div>
                                    <div className="stats">
                                        <h3 >Description</h3>
                                        <p>{templateTask.description}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Task Organization</h3>
                                        <p>{templateTask.taskOrganization.type}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Task Type</h3>
                                        <p>{templateTask.taskType.type}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Task Sub-Type</h3>
                                        <p>{templateTask.taskSubType.type}</p>
                                    </div>
                                </div>
                                <div className="top border_tittle">
                                    <div className="top_bar_tittle">
                                        <h2>Scheduling Definitions{" "}</h2>
                                    </div>
                                </div>
                                <div className="edit_template_grid">
                                    <div className="stats">
                                        <h3>Distribution Pattern:</h3>{" "}
                                        <p>{templateTask.distributionPattern}</p>
                                    </div>
                                    {templateTask.waiverName && (
                                        <div className="stats">
                                            <h3>Waiver Type</h3>{" "}
                                            <p>{templateTask.waiverName}</p>
                                        </div>
                                    )}
                                    <div className="stats">
                                        <h3>Due Date Calculation Type</h3>{" "}
                                        <p>{templateTask.dueDateCalculationType}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Due Date Offset:</h3>{" "}
                                        <p>{templateTask.dueDateOffset}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Due Date Cron</h3>{" "}
                                        <p>{templateTask.dueDateCron}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Creation Date Calculation Type</h3>{" "}
                                        <p>{templateTask.creationDateCalculationType}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Creation Date Offset</h3>{" "}
                                        <p>{templateTask.creationDateOffset}</p>
                                    </div>
                                    <div className="stats">
                                        <h3>Creation Date Cron:</h3>{" "}
                                        <p>{templateTask.creationCron ? templateTask.creationCron : "<undefined>"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mb-3">
                            <AddTaskAction taskId={templateTask.id} reload={reloadFn} />
                        </div>
                        <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mb-3">
                            {templateTask && (
                                <div className="common_card h-100">
                                    <div className="top border_tittle">
                                        <div className="top_bar_tittle">
                                            <h2>Task Tags</h2>
                                        </div>
                                    </div>
                                    <div className="custom_input_flled mb-0">
                                        <Select
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            options={tagOptions}
                                            isMulti
                                            onChange={handleTagChange}
                                            value={tagOptions.filter((opt) =>
                                                selectedTagIds.includes(parseInt(opt.value))
                                            )}
                                        />
                                    </div>
                                    <div className="form_btn_flx mby_t_24">
                                        <Button variant="primary" onClick={submitTaskTags}>Add</Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mb-3">
                            {templateFiles.length > 0 && (
                                <div className="common_card h-100">
                                    <div className="top border_tittle">
                                        <div className="top_bar_tittle">
                                            <h2>Required Files</h2>
                                        </div>
                                    </div>
                                    <div className="task_action_attchment_file_flx">
                                        {templateFiles.map((file, index) => (
                                            <div
                                                key={index}
                                                className="attchment_file task_action_attchment_file"
                                            >
                                                <div className="attchment_file_icon">
                                                    <img style={{ width: '1.5rem' }} src={pdficon} alt="pdficon" />
                                                </div>{" "}
                                                <div className="attchment_file_txt">
                                                    <p>{file.templateFile?.fileName}</p>
                                                </div>
                                                <button className="attchment_file_top_icon" onClick={() => deleteTaskFile(file.id)}>
                                                    <img src={trashicons} alt="trashicons" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mb-3">
                            <AddRequiredFile taskId={templateTask.id} reload={reloadFn} />
                        </div>
                        {templateTaskActions.length > 0 && (
                            <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mb-3">
                                <div className="common_card h-100">
                                    <div className="top border_tittle">
                                        <div className="top_bar_tittle">
                                            <h2>Task Actions</h2>
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        {templateTaskActions.map((action, index) => (
                                            <div
                                                key={index}
                                                className="d-flex justify-content-between align-items-center p-2 mb-2"
                                                style={{ background: '#f8f9fa', borderRadius: '5px' }}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <FontAwesomeIcon icon={faBolt} className="text-primary" />
                                                    <span>{action.description}</span>
                                                </div>
                                                <button
                                                    className="text-danger cursor-pointer"
                                                    onClick={() => deleteTaskAction(action.id)}
                                                    style={{ background: 'none', border: 'none' }}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        <div className="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-6 mb-3">
                            <UpdateInformation 
                                informationData={templateTask.information || ""}
                                taskTemplateId={templateTask.id}                            />
                        </div>
                        <div className="col-12 mb-3">
                            <div className="common_card">
                                <div className="custom_input_flled">
                                    <label>Task Relationships <span>(Optional)</span>:</label>
                                    <div className="card">
                                        <div className="card-body">
                                            {existingRelationships.length > 0 ? (
                                                existingRelationships.map(rel => (
                                                    <div key={`${rel.otherTemplateId}-${rel.relationshipTypeId}`} className="d-flex justify-content-between align-items-center mb-2">
                                                        <span>{`This task ${rel.relationshipName.toLowerCase()} '${rel.otherTemplateTitle}'`}</span>
                                                        <Button variant="danger" size="sm" onClick={() => handleRemoveRelationship(rel.otherTemplateId, rel.relationshipTypeId)}>Remove</Button>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-muted">No relationships defined.</div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="mt-2">
                                        {isAddingRelationship ? (
                                            <div className="d-flex">
                                                <div style={{ flex: 1, marginRight: '0.5rem' }}>
                                                    <Select
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        options={relationshipTypes.map(rt => ({ value: rt.id, label: rt.name }))}
                                                        onChange={(option) => setNewRelationshipTypeId(option ? Number(option.value) : null)}
                                                        placeholder="Select relationship type..."
                                                    />
                                                </div>
                                                <div style={{ flex: 1, marginLeft: '0.5rem' }}>
                                                    <Select
                                                        className="react-select-container"
                                                        classNamePrefix="react-select"
                                                        options={allInterimTemplates.map(t => ({ value: t.id, label: t.title }))}
                                                        onChange={(option) => setNewRelationshipTargetId(option ? Number(option.value) : null)}
                                                        placeholder="Select a task..."
                                                    />
                                                </div>
                                                <Button variant="primary" onClick={handleAddRelationship} className="ms-2">Add</Button>
                                                <Button variant="secondary" onClick={() => setIsAddingRelationship(false)} className="ms-2">Cancel</Button>
                                            </div>
                                        ) : (
                                            <Button variant="secondary" onClick={() => setIsAddingRelationship(true)}>Add Relationship</Button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <div className="form_btn_flx mby_t_24">
                                <Button variant="primary" type="submit">Save Template</Button>
                            </div>
                        </div>
                    </div>
                </form>
            )}
        </>
    );
};

export default TemplateTaskEditor;
